import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/api/query-keys';

import {
  approveReplacement,
  getReplacement,
  listReplacementCategories,
  listReplacementCities,
  rejectReplacement,
} from './api';
import type { ApproveReplacementBody, RejectReplacementValues } from './types';

/** Query-key factory for the replacements feature. */
export const replacementKeys = {
  all: queryKeys.replacements.all,
  detail: (id: string) => [...replacementKeys.all, 'detail', id] as const,
  metaCities: () => [...replacementKeys.all, 'meta', 'cities'] as const,
  /** Under the Category root so a Category write invalidates the filter's options. */
  metaCategories: () => queryKeys.categories.meta('replacements', { status: 'active' }),
  /** Cursor typeaheads keyed by search term so a new term starts a fresh first page. */
  metaCustomers: (search: string) => queryKeys.customers.meta('replacements', { search }),
  metaDevices: (search: string) => queryKeys.devices.meta('replacements', { search }),
};

/** A decision also changes the Order it belongs to — its Replacement requests panel, and on approve
 *  the new $0.00 replacement Order — and the Dashboard's open-request counts. The order id is not
 *  known here, so the Order root is invalidated whole. */
function invalidateDecisionReaders(qc: ReturnType<typeof useQueryClient>, id: string) {
  for (const queryKey of [
    replacementKeys.detail(id),
    replacementKeys.all,
    queryKeys.orders.all,
    queryKeys.dashboard.quickActions,
  ]) {
    void qc.invalidateQueries({ queryKey });
  }
}

/** Single replacement-request detail; idle while `id` is empty (drawer closed). */
export function useReplacement(id: string) {
  return useQuery({
    queryKey: replacementKeys.detail(id),
    queryFn: () => getReplacement(id),
    enabled: Boolean(id),
  });
}

/** Rejects the replacement request with an admin-entered reason. The real backend's reject
 *  response is slim (`{id, status}`), so this invalidates rather than seeds the detail cache —
 *  row + drawer both refetch and show Rejected. */
export function useRejectReplacement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: RejectReplacementValues) => rejectReplacement(id, values),
    onSuccess: () => invalidateDecisionReaders(qc, id),
  });
}

/** Approves the replacement request (with or without an installation request). The real
 *  backend's approve response is slim, so this invalidates rather than seeds the detail cache —
 *  row + drawer both refetch and show Approved. */
export function useApproveReplacement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApproveReplacementBody) => approveReplacement(id, body),
    onSuccess: () => invalidateDecisionReaders(qc, id),
  });
}

/** City filter options; cached 5 min (changes rarely). */
export function useReplacementCities() {
  return useQuery({
    queryKey: replacementKeys.metaCities(),
    queryFn: listReplacementCities,
    staleTime: 5 * 60 * 1000,
  });
}

/** Device Category filter options; cached 5 min. */
export function useReplacementCategories() {
  return useQuery({
    queryKey: replacementKeys.metaCategories(),
    queryFn: listReplacementCategories,
    staleTime: 5 * 60 * 1000,
  });
}
